import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { companies } from "../../data/content";
import ThemeToggle from "./ThemeToggle";

const linkCls = ({ isActive }) =>
  `text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 ${
    isActive ? "text-gold" : "text-white/75 hover:text-white"
  }`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const solid = scrolled || megaOpen || mobileOpen || location.pathname.startsWith("/admin");

  return (
    <>
      <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid ? "border-b border-white/10 bg-ink/95 backdrop-blur-md" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="flex h-20 items-center justify-between px-6 lg:px-12">
        <Link to="/" data-testid="logo-link" className="flex items-center" aria-label="Hapo Group home">
          <img src="/assets/logos/hapo-white.webp" alt="Hapo Group" className="only-dark-theme h-7 w-auto" />
          <img src="/assets/logos/hapo.webp" alt="Hapo Group" className="only-light-theme h-7 w-auto" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">
            Home
          </NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">
            About
          </NavLink>
          <div onMouseEnter={() => setMegaOpen(true)} onMouseLeave={() => setMegaOpen(false)}>
            <button
              type="button"
              data-testid="nav-companies"
              aria-expanded={megaOpen}
              aria-haspopup="true"
              onClick={() => setMegaOpen((o) => !o)}
              onKeyDown={(e) => e.key === "Escape" && setMegaOpen(false)}
              className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 ${
                location.pathname.startsWith("/companies") ? "text-gold" : "text-white/75 hover:text-white"
              }`}
            >
              Our Companies
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          <NavLink to="/solutions" className={linkCls} data-testid="nav-solutions">
            Solutions
          </NavLink>
          <NavLink to="/partnerships" className={linkCls} data-testid="nav-partnerships">
            Partnerships
          </NavLink>
          <NavLink to="/careers" className={linkCls} data-testid="nav-careers">
            Careers
          </NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">
            Contact
          </NavLink>
          <ThemeToggle />
          <Link
            to="/contact"
            data-testid="nav-get-in-touch"
            className="rounded-full bg-gold px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:bg-white hover:scale-105"
          >
            Get in Touch
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <ThemeToggle />
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((o) => !o)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {megaOpen && (
          <motion.div
            key="mega"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
            className="absolute inset-x-0 top-full hidden border-b border-white/10 bg-ink lg:block"
            data-testid="companies-mega-menu"
          >
            <div className="grid grid-cols-3 gap-px bg-white/5 px-6 py-2 lg:px-12">
              {companies.map((c) => (
                <Link
                  key={c.slug}
                  to={`/companies/${c.slug}`}
                  data-testid={`mega-${c.slug}`}
                  className="group bg-ink px-6 py-8 transition-colors duration-300 hover:bg-white/[0.04]"
                >
                  {c.logoOnDark && (
                    <>
                      <img src={c.logoOnDark} alt="" className="only-dark-theme mb-5 h-6 w-auto" />
                      <img src={c.logoOnLight} alt="" className="only-light-theme mb-5 h-6 w-auto" />
                    </>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-white">{c.name}</span>
                    <ArrowUpRight className="h-4 w-4 text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-smoke">{c.cardTag}</p>
                  <span className="mt-4 inline-block text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                    {c.stage}
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-20 z-40 overflow-y-auto bg-ink px-6 pb-16 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-7 pt-10" aria-label="Mobile">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About" },
                { to: "/companies", label: "Our Companies" },
                { to: "/solutions", label: "Solutions" },
                { to: "/partnerships", label: "Partnerships" },
                { to: "/careers", label: "Careers" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `text-3xl font-extrabold tracking-tight ${isActive ? "text-gold" : "text-white"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-6 border-t border-white/10 pt-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">Our Companies</p>
                <div className="mt-5 flex flex-col gap-4">
                  {companies.map((c) => (
                    <NavLink
                      key={c.slug}
                      to={`/companies/${c.slug}`}
                      data-testid={`mobile-nav-${c.slug}`}
                      className="text-sm font-medium text-white/70"
                    >
                      {c.name}
                    </NavLink>
                  ))}
                </div>
              </div>
              <Link
                to="/contact"
                data-testid="mobile-get-in-touch"
                className="mt-8 inline-flex w-max items-center rounded-full bg-gold px-8 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-ink"
              >
                Get in Touch
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
